import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import { ConsultationsConsumerRenderProps } from 'components/consultations/ConsultationsContext';
import HomeTableHeaderCell from 'components/layout/HomeTableHeaderCell';
import ToolbarButton from 'components/layout/ToolbarButton';
import LoaderComponent from 'components/LoaderComponent';
import { PatientConsumerRenderProps } from 'components/patient/PatientContext';
import { TreatmentsConsumerRenderProps } from 'components/treatments/TreatmentsContext';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Popup, Table } from 'semantic-ui-react';
import { getAllConsultationEntriesByConsultationId, printMedicamentLabel } from 'services/consultationServices';
import styled from 'styled-components';
import { ConsultationDto, ConsultationEntryDto } from 'ts-types/api.types';
import {
  ChromeExtensionStatus,
  getChromeExtensionStatus,
  getPrinters,
  printPatientMedicamentLabel
} from 'utils/chomeExtensionUtils';
import { numberFormatter } from 'utils/fomattingUtils';
import { noop } from 'utils/functionUtils';

const ProceduresInfoContainer = styled.div`
  flex-grow: 1;
  cursor: context-menu;
  overflow: auto;
  max-height: 270px;

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }
`;

const InfoDiv = styled.div`
  & .info-message {
    margin-top: 2rem;
    min-height: 105px;
  }

  & .info-message .download-link {
    margin-top: 1.5rem;
  }

  & .actions .button:first-child {
    margin-left: 0;
  }
`;

interface Props extends
    AuthConsumerRenderProps,
    MessageSourceProps,
    ConsultationsConsumerRenderProps,
    TreatmentsConsumerRenderProps,
    PatientConsumerRenderProps {

}

interface State {
  currentProcedures: Array<ConsultationEntryDto>,
  selectedProcedure?: ConsultationEntryDto,
  loadingInProgress: boolean,
  printLabelPopupOpen: boolean,
  printLabelPopupStatus: string,
  cancelTokenSource: CancelTokenSource
}

class ProceduresInfo extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      currentProcedures: [],
      selectedProcedure: undefined,
      loadingInProgress: false,
      printLabelPopupOpen: false,
      printLabelPopupStatus: "none",
      cancelTokenSource

    }
  }

  componentDidMount(): void {
    const {selectedConsultation, selectedTreatment} = this.props;
    this.mounted = true;

    if (selectedTreatment && selectedConsultation) {
      this.getProcedures(selectedConsultation);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<any>, snapshot?: any): void {
    const {currentProcedures} = this.state;
    const {selectedConsultation, selectedTreatment} = this.props;

    if (prevProps.selectedConsultation != selectedConsultation) {
      this.getProcedures(selectedConsultation)
    } else if ((!selectedTreatment || !selectedConsultation) && currentProcedures.length != 0) {
      this.setState({
        currentProcedures: []
      })
    }
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  getProcedures(consultation: ConsultationDto | undefined): void {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;

    this.setState({
      currentProcedures: [],
      selectedProcedure: undefined,
      loadingInProgress: true

    });

    const onFinally = () => {
      this.setState({
        loadingInProgress: false
      })
    };

    if (consultation) {
      getAllConsultationEntriesByConsultationId(consultation.id, cancelTokenSource).then(response => {
            const currentConsultationProps = this.props.selectedConsultation;
            if (!currentConsultationProps || consultation.id === currentConsultationProps.id) {
              this.setState({
                currentProcedures: response,
                selectedProcedure: response[0]
              });
            }
          })
          .catch(noop)
          .finally(onFinally)
    } else {
      this.setState({
        currentProcedures: [],
        selectedProcedure: undefined,
        loadingInProgress: false
      });
    }
  }

  markProcedure = (procedureId: number) => {
    const foundProcedure = this.state.currentProcedures
        .find((procedure: ConsultationEntryDto) => procedure.id === procedureId);

    this.setState({
      selectedProcedure: foundProcedure
    });
  };

  triggerPrintPatientMedicamentLabel = async (consultationEntryId: number, patientId: number) => {
    const {config} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      printLabelPopupOpen: true,
      printLabelPopupStatus: "STARTED"
    })

    const extStatus = await getChromeExtensionStatus(config);

    if (ChromeExtensionStatus.NO_EXTENSION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OUTDATED_EXTENSION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.NO_HOST_APPLICATION === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OUTDATED_HOST_APP === extStatus) {
      this.setState({
        printLabelPopupStatus: extStatus
      });
    } else if (ChromeExtensionStatus.OK === extStatus) {
      this.setState({
        printLabelPopupStatus: "RETRIEVING_PRINTERS"
      });

      const printersResult: any = await getPrinters();
      const printers = printersResult.payload;
      // @ts-ignore
      const labelPrinter: any = printers.find(printer => {
        const printerName: string = printer.name.toLowerCase();
        return printerName.includes("dymo") && printerName.includes("450");
      });

      if (labelPrinter) {
        this.setState({
          printLabelPopupStatus: "CREATING_LABELS"
        });

        const medicamentLabel = await printMedicamentLabel(consultationEntryId, patientId, cancelTokenSource);

        this.setState({
          printLabelPopupStatus: "PRINTING_LABELS"
        });

        await printPatientMedicamentLabel(medicamentLabel, labelPrinter.name);

        this.setState({
          printLabelPopupStatus: "DONE"
        });
      } else {
        this.setState({
          printLabelPopupStatus: "PRINTER_NOT_FOUND"
        });
      }
    }
  };

  closePrintLabelPopup = () => {
    this.setState({
      printLabelPopupOpen: false
    });
  }

  printLabelPopupContent = () => {
    const {getMessage} = this.props;
    const {printLabelPopupStatus} = this.state;

    let content = () => {
      switch (printLabelPopupStatus.toUpperCase()) {
        case 'STARTED' :
          return <div>...</div>;

        case 'NO_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_EXTENSION')}
              <div className="download-link">
                <a href="https://chrome.google.com/webstore/detail/iggeppehjnmjeeicldkpjopdofglljei?authuser=0&hl=en"
                   target="_blank">{getMessage('nativeTools.download.chromeExtension')}</a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_EXTENSION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_EXTENSION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.chromeExtension')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'NO_HOST_APPLICATION' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.NO_HOST_APPLICATION')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'OUTDATED_HOST_APP' :
          return <InfoDiv>
            <div className="info-message">
              {getMessage('nativeTools.status.OUTDATED_HOST_APP')}
              <div className="download-link">
                <a href="https://praxmedic.ch/native-tools" target="_blank">
                  {getMessage('nativeTools.download.hostApplication')}
                </a>
              </div>
            </div>
          </InfoDiv>;

        case 'PRINTER_NOT_FOUND' :
          return <div>The label printer was not found.</div>;

        case 'RETRIEVING_PRINTERS' :
          return <div>Retrieving available printers...</div>;

        case 'CREATING_LABELS' :
          return <div>Creating labels...</div>;

        case 'PRINTING_LABELS' :
          return <div>Printing labels...</div>;

        case 'DONE' :
          return <div>Labels printed.</div>;
      }
    }

    return (
        <div>
          {content()}
          <div>
            <Button type="button" compact onClick={this.closePrintLabelPopup}>Close</Button>
          </div>
        </div>
    )
  }

  renderProcedureInfoTable(): React.ReactNode {

    const {selectedProcedure, currentProcedures, printLabelPopupOpen} = this.state;
    const {currentPatient, getMessage} = this.props;
    const selectedProcedureId = selectedProcedure ? selectedProcedure.id : -1;
    const selectedProcedureMedicamentId = selectedProcedure ? selectedProcedure.medicamentId : -1;

    return (
        <ProceduresInfoContainer>
          <Table basic='very' celled padded selectable compact>
            <Table.Header>
              <Table.Row>
                <HomeTableHeaderCell
                    verticalAlign="middle">{getMessage('procedure.quantity.short')}</HomeTableHeaderCell>
                <HomeTableHeaderCell
                    verticalAlign="middle">{getMessage('procedure.code')}</HomeTableHeaderCell>
                <HomeTableHeaderCell
                    verticalAlign="middle">{getMessage('procedure.description')}</HomeTableHeaderCell>
                <HomeTableHeaderCell verticalAlign="middle">{getMessage('value')}</HomeTableHeaderCell>
                <HomeTableHeaderCell
                    verticalAlign="middle">{getMessage('procedure.type')}</HomeTableHeaderCell>
              </Table.Row>
            </Table.Header>
            {currentProcedures && (
                <Table.Body>
                  {
                    currentProcedures.map(procedure => {
                      return (
                          <Table.Row
                              key={procedure.id}
                              onClick={() => this.markProcedure(procedure.id)}
                              active={procedure.id == selectedProcedureId}>
                            <Table.Cell
                                textAlign="right">{numberFormatter.format(procedure.quantity)}</Table.Cell>
                            <Table.Cell
                                textAlign="right">{getMessage(procedure.code)}</Table.Cell>
                            <Table.Cell>
                              <div style={{display: "flex"}}>
                                {getMessage(procedure.description)}
                                {procedure.medicamentId &&
                                    <Popup
                                        trigger={
                                          <ToolbarButton
                                              iconName="print"
                                              iconSize="small"
                                              onClick={() => this.triggerPrintPatientMedicamentLabel(
                                                  procedure.id, currentPatient!.id)
                                          }
                                              dataTooltip={getMessage("procedure.medicamentLabel.printButton")}
                                          />
                                        }
                                        open={printLabelPopupOpen &&
                                            procedure.medicamentId === selectedProcedureMedicamentId}
                                        content={this.printLabelPopupContent()}
                                        on="click"
                                        onOpen={() => {
                                        }}
                                        onClose={() => {
                                        }}
                                        size="small"
                                        position="bottom center"
                                    />
                                }
                              </div>
                            </Table.Cell>
                            <Table.Cell textAlign="right">{numberFormatter.format(procedure.value)}</Table.Cell>
                            <Table.Cell>{procedure.type}</Table.Cell>
                          </Table.Row>
                      );
                    })
                  }
                </Table.Body>
            )}
          </Table>
        </ProceduresInfoContainer>
    );
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;

    const {loadingInProgress} = this.state;
    if (loadingInProgress) {
      return (
          <LoaderComponent message={getMessage("procedures.loader")}/>
      );
    }
    return (
        this.renderProcedureInfoTable()
    );
  }
}

export default withAuthContext(withMessages(ProceduresInfo));
